import axios from "axios";
import { convertToSQLDate } from "../helpers";
import Vue from "vue";
import modules from "../modules";
import { cloneDeep } from "lodash";

// Accounts that are locked to the City Express screen only. These are regular
// super_admin users, but the UI hides everything except City Express for them
// (e.g. logistika@bigznet.rs). Any other super_admin keeps full access.
const CITY_EXPRESS_ONLY_EMAILS = [
  "logistika@bigznet.rs",
  "magacin@bigznet.rs",
  "borisbterzic@gmail.com",
];

export const state = {
  user: {},
  account: {},
  categories: [],
  instagram: {
    overview: {},
    posts: {},
    stories: {},
  },
  facebook: {
    overview: {},
    posts: {},
  },
  youtube: {
    overview: {},
    posts: {},
  },
  blog: {
    overview: {},
  },
  overviewPeriod: {
    instagram: 7,
    facebook: 7,
    youtube: 7,
    blog: 7,
  },
};

export const mutations = {
  SET_USER(state, newValue) {
    state.user = newValue;
  },
  UPDATE_USER(state, { fieldName, value }) {
    Vue.set(state.user, fieldName, value);
  },
  SET_ACCOUNT(state, newValue) {
    state.account = newValue;
  },
  UPDATE_ACCOUNT(state, newData) {
    Object.assign(state.account, newData);
  },
  SET_CATEGORIES(state, categories) {
    state.categories = categories;
  },
  SET_INSTAGRAM(state, instagram) {
    state.instagram = instagram;
  },
  SET_FACEBOOK(state, facebook) {
    state.facebook = facebook;
  },
  SET_YOUTUBE(state, youtube) {
    state.youtube = youtube;
  },
  SET_BLOG(state, blog) {
    state.blog = blog;
  },
  CLEAR(state) {
    if (Object.keys(state.user).length > 0) {
      state.user = {};
      state.account = {};
      state.categories = [];
      state.instagram = {
        overview: {},
        posts: {},
        stories: {},
      };
      state.facebook = {
        overview: {},
        posts: {},
      };
      state.youtube = {
        overview: {},
        posts: {},
      };
      state.blog = {
        overview: {},
      };
      state.overviewPeriod = {
        instagram: 7,
        facebook: 7,
        youtube: 7,
      };
    }
  },
  SET_SOCIAL_FIELD(state, { platform, field, payload }) {
    state[platform][field] = payload;
  },
  SET_SOCIAL_OVERVIEW_PERIOD(state, { platform, range }) {
    state.overviewPeriod[platform] = range;
  },
};

export const getters = {
  userId(state) {
    return state.user?._id;
  },
  hasUser(state) {
    return Object.keys(state.user).length > 0;
  },
  setupFinished(state) {
    if (getters.userType(state) === "admin") {
      return true;
    }
    // phone number for an influencer and contact person for a client
    return state.user.phone_number || state.user.contact_person;
  },
  emailVerified(state) {
    if (getters.userType(state) === "admin") {
      return true;
    }

    return !!state.account.email_verified_at;
  },
  clientApproved(state) {
    return (
      getters.userType(state) === "client" && state.user.client.status === 1
    );
  },
  homeRouteName(state) {
    if (getters.isCityExpressOnly(state)) {
      return "super_admin_city_express_v2";
    }
    if (getters.userType(state) === "super_admin") {
      return "super_admin_home";
    }
    if (getters.userType(state) === "admin") {
      return "order_form_items";
    }
    if (getters.userType(state) === "employee") {
      return "employee_order_form";
    }
    return getters.userType(state) + "_home";
  },
  userType(state) {
    return state.account.type;
  },
  isCityExpressOnly(state) {
    const email = (state.account?.email || state.user?.email || "")
      .toString()
      .trim()
      .toLowerCase();
    return CITY_EXPRESS_ONLY_EMAILS.includes(email);
  },
  userManual(state) {
    return state.account.help_document_link;
  },
  orderDeadline(state) {
    return state.account.order_deadline;
  },
  hasInstagramData(state) {
    return Object.keys(state.instagram.overview).length > 0;
  },
  hasFacebookData(state) {
    return Object.keys(state.facebook.overview).length > 0;
  },
  hasYoutubeData(state) {
    return Object.keys(state.youtube.overview).length > 0;
  },
  hasBlogData(state) {
    return Object.keys(state.blog.overview).length > 0;
  },
  connectedSocialNetworks(state) {
    if (getters.userType(state) === "influencer") {
      return ["facebook", "instagram", "youtube", "blog"].filter(
        (value) =>
          Object.keys(state.user.social_network_accounts).includes(value) &&
          state.user.social_network_accounts[value],
      );
    }
    return [];
  },
  instagramConnected(state) {
    return (
      state.user.social_network_accounts &&
      state.user.social_network_accounts.instagram
    );
  },
  facebookConnected(state) {
    return (
      state.user.social_network_accounts &&
      state.user.social_network_accounts.facebook
    );
  },
  youtubeConnected(state) {
    return (
      state.user.social_network_accounts &&
      state.user.social_network_accounts.youtube
    );
  },
  blogConnected(state) {
    return (
      state.user.social_network_accounts &&
      state.user.social_network_accounts.blog
    );
  },
  hasInstagramTokenError(state) {
    if (getters.instagramConnected(state)) {
      return !!state.user.social_network_accounts.instagram.access_token_error;
    }
    return false;
  },
  hasFacebookTokenError(state) {
    if (getters.facebookConnected(state)) {
      return !!state.user.social_network_accounts.facebook.access_token_error;
    }
    return false;
  },
};

export const actions = {
  async getCategories({ commit }) {
    commit("loader/START_LOADING", null, { root: true });
    try {
      if (localStorage.getItem("categories")) {
        commit(
          "SET_CATEGORIES",
          JSON.parse(localStorage.getItem("categories")),
        );
      } else {
        const { data } = await axios.get("/profile-category");
        commit("SET_CATEGORIES", data);
        localStorage.setItem("categories", JSON.stringify(data));
      }
    } finally {
      commit("loader/END_LOADING", null, { root: true });
    }
  },
  async getUserData({ commit }) {
    commit("loader/START_LOADING", null, { root: true });
    try {
      const userRequest = axios.get("/user");

      const responses = await axios.all([userRequest]);
      const accountData = responses[0].data;
      commit("SET_ACCOUNT", accountData);
      commit("SET_USER", accountData);
    } finally {
      commit("loader/END_LOADING", null, { root: true });
    }
  },
  async getInstagramData({ commit }) {
    commit("loader/START_LOADING", null, { root: true });
    let date = new Date();
    date.setDate(date.getDate() - (7 - 1));
    try {
      const { data: overview } = await axios.get(
        `/profile/instagram/overview?since=${convertToSQLDate(date)}`,
      );
      const { data: posts } = await axios.get(
        `/profile/instagram/posts?post_type=FEED`,
      );
      const { data: stories } = await axios.get(
        `/profile/instagram/posts?post_type=STORY`,
      );
      commit("SET_INSTAGRAM", { overview, posts, stories });
    } finally {
      commit("loader/END_LOADING", null, { root: true });
    }
  },
  async getFacebookData({ commit }) {
    commit("loader/START_LOADING", null, { root: true });
    let date = new Date();
    date.setDate(date.getDate() - (7 - 1));
    try {
      const { data: overview } = await axios.get(
        `/profile/facebook/overview?since=${convertToSQLDate(date)}`,
      );
      const { data: posts } = await axios.get(`/profile/facebook/posts`);
      commit("SET_FACEBOOK", { overview, posts });
    } finally {
      commit("loader/END_LOADING", null, { root: true });
    }
  },
  async getYoutubeData({ commit }) {
    commit("loader/START_LOADING", null, { root: true });
    let date = new Date();
    date.setDate(date.getDate() - (7 - 1));
    try {
      const { data: overview } = await axios.get(
        `/profile/youtube/overview?since=${convertToSQLDate(date)}`,
      );
      const { data: posts } = await axios.get(`/profile/youtube/posts`);
      commit("SET_YOUTUBE", { overview, posts });
    } finally {
      commit("loader/END_LOADING", null, { root: true });
    }
  },
  async getBlogData({ commit }) {
    commit("loader/START_LOADING", null, { root: true });
    let date = new Date();
    date.setDate(date.getDate() - (7 - 1));
    try {
      const { data: overview } = await axios.get(
        `/profile/blog/overview?since=${convertToSQLDate(date)}`,
      );
      commit("SET_BLOG", { overview });
    } finally {
      commit("loader/END_LOADING", null, { root: true });
    }
  },
  async getInstagramPosts({ commit }, { pageNumber, type }) {
    commit("loader/START_LOADING", null, { root: true });
    try {
      const { data: posts } = await axios.get(
        `/profile/instagram/posts?post_type=${type}&page=${pageNumber}`,
      );
      return posts.data;
    } finally {
      commit("loader/END_LOADING", null, { root: true });
    }
  },
  async getFacebookPosts({ commit }, { pageNumber }) {
    commit("loader/START_LOADING", null, { root: true });
    try {
      const { data: posts } = await axios.get(
        `/profile/facebook/posts?page=${pageNumber}`,
      );
      return posts.data;
    } finally {
      commit("loader/END_LOADING", null, { root: true });
    }
  },
  async getSocialOverview({ commit }, { sinceDate, platform }) {
    commit("loader/START_LOADING", null, { root: true });
    try {
      const { data: overview } = await axios.get(
        `/profile/${platform}/overview?since=${convertToSQLDate(sinceDate)}`,
      );
      commit("SET_SOCIAL_FIELD", {
        platform,
        field: "overview",
        payload: overview,
      });
    } finally {
      commit("loader/END_LOADING", null, { root: true });
    }
  },
  clearState({ commit }) {
    for (let [moduleName, module] of Object.entries(modules)) {
      if (module.mutations && module.mutations.CLEAR) {
        commit(`${moduleName}/CLEAR`, null, { root: true });
      }
    }
  },
  async switchToFixedPrices({ commit }) {
    commit("pricing/CLEAR", null, { root: true });
    commit("UPDATE_USER", { fieldName: "pricing_on_demand", value: false });
  },
  async switchToOnDemandPricing({ commit, state }) {
    commit("pricing/CLEAR", null, { root: true });
    commit("loader/START_LOADING", null, { root: true });
    try {
      const updatedUser = {
        ...cloneDeep(state.user),
        pricing_on_demand: true,
      };
      delete updatedUser._id;
      await axios.put("/profile", updatedUser);
      commit("UPDATE_USER", { fieldName: "pricing_on_demand", value: true });
    } finally {
      commit("loader/END_LOADING", null, { root: true });
    }
  },
  async updateAccount({ commit }, payload) {
    const response = await axios.put("/user/password", payload);
    commit("UPDATE_ACCOUNT", response.data);
  },
};
